import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Dropdown, Menu, Modal, Form, Input, message, Avatar, Badge, Spin } from 'antd';
import { UserOutlined, BellOutlined, LogoutOutlined, LockOutlined, CheckOutlined } from '@ant-design/icons';
import apiClient from '../api/apiClient';

const { Header } = Layout;

const HeaderComponent = () => {
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState({}); // { 1: 'Administrador', 2: 'Operador', ... }
  const [notifications, setNotifications] = useState([]);
  const [loadingNotif, setLoadingNotif] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  // Cargar usuario y roles
  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) {
      const parsed = JSON.parse(stored);
      setUser({
        id: parsed.IdUsuario,
        nombreUsuario: parsed.NombreUsuario,
        nombreCompleto: parsed.NombreCompleto || 'Sin nombre completo',
        rol: parsed.rol || parsed.IdRol
      });
    }

    // Cargar roles desde API
    const fetchRoles = async () => {
      try {
        const res = await apiClient.get('/roles');
        if (res.data.success) {
          const rolesMap = {};
          res.data.data.forEach(r => {
            rolesMap[r.IdRol] = r.NombreRol;
          });
          setRoles(rolesMap);
        }
      } catch (error) {
      }
    };

    fetchRoles();
  }, []);

  // Cargar notificaciones para docentes (rol 4)
  const cargarNotificaciones = useCallback(async () => {
    if (!user || user.rol !== 4) return;
    setLoadingNotif(true);
    try {
      const res = await apiClient.post('/notificaciones-docentes/generar-calificaciones-atrasadas');
      if (res.data.success && res.data.notificacionesPendientes) {
        setNotifications(res.data.notificacionesPendientes);
      } else {
        setNotifications([]);
      }
    } catch (error) {
      setNotifications([]);
    } finally {
      setLoadingNotif(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && user.rol === 4) {
      cargarNotificaciones();
    }
  }, [user, cargarNotificaciones]);

  // Marcar una notificación como leída
  const marcarLeida = async (idNotificacion) => {
    try {
      await apiClient.put('/notificaciones-docentes/marcar-leida', {
        IdNotificacion: idNotificacion
      });
      // Remover de la lista local
      setNotifications(prev => prev.filter(n => n.IdNotificacion !== idNotificacion));
    } catch (error) {
      message.error('Error al marcar notificación como leída');
    }
  };

  // Marcar todas como leídas
  const marcarTodasLeidas = async () => {
    try {
      await apiClient.put('/notificaciones-docentes/marcar-leida', {
        Todas: true
      });
      setNotifications([]);
      message.success('Todas las notificaciones marcadas como leídas');
    } catch (error) {
      message.error('Error al marcar notificaciones');
    }
  };

  if (!user) {
    return (
      <Header style={{ background: '#001f3f', color: '#fff', textAlign: 'right', padding: '0 24px' }}>
        Cargando...
      </Header>
    );
  }

  const idUsuario = user.id;
  const rolColores = {
    1: '#ff4d4f', // Administrador: rojo
    2: '#1890ff', // Operador: azul
    3: '#52c41a', // Familia: verde
    4: '#fa8c16', // Docente: naranja
  };

  const colorRol = rolColores[user.rol] || '#8c8c8c';
  // Cambio de contraseña
  const handleChangePassword = async (values) => {
    try {
      const payload = {
        Contrasena: values.newPassword,
        ContrasenaActual: values.currentPassword,
        IdColaborador: idUsuario
      };
      const response = await apiClient.put(`/usuarios/${encodeURIComponent(idUsuario)}`, payload);
      if (response.data.success) {
        message.success('Contraseña cambiada exitosamente');
        handleCancel();
      }
    } catch (error) {
      const msg = error.response?.data?.error || 'Error al cambiar contraseña';
      message.error(msg);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  const notificationMenu = (
    <Menu style={{ width: 380, maxHeight: 400, overflow: 'auto' }}>
      {loadingNotif ? (
        <Menu.Item key="loading" disabled>
          <div style={{ textAlign: 'center', padding: 12 }}>
            <Spin size="small" /> Cargando notificaciones...
          </div>
        </Menu.Item>
      ) : notifications.length > 0 ? (
        <>
          {notifications.map((notif) => (
            <Menu.Item
              key={notif.IdNotificacion}
              onClick={() => marcarLeida(notif.IdNotificacion)}
              style={{ whiteSpace: 'normal', height: 'auto', lineHeight: '1.4' }}
            >
              <div style={{ padding: '4px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <strong style={{ color: '#fa8c16', fontSize: 12 }}>
                    {notif.NombreCurso || 'Calificación atrasada'}
                  </strong>
                  {notif.NombreUnidad && (
                    <span style={{ fontSize: 11, color: '#999' }}>{notif.NombreUnidad}</span>
                  )}
                </div>
                <p style={{ margin: 0, fontSize: 12, color: '#333' }}>{notif.Mensaje}</p>
                <small style={{ color: '#999' }}>
                  {notif.FechaCreacion ? new Date(notif.FechaCreacion).toLocaleDateString('es-GT') : ''}
                  {' — Click para marcar como leída'}
                </small>
              </div>
            </Menu.Item>
          ))}
          <Menu.Divider />
          <Menu.Item
            key="marcar-todas"
            onClick={marcarTodasLeidas}
            style={{ textAlign: 'center' }}
          >
            <CheckOutlined style={{ marginRight: 6 }} />
            <strong>Marcar todas como leídas</strong>
          </Menu.Item>
        </>
      ) : (
        <Menu.Item key="0" disabled>
          <div style={{ textAlign: 'center', color: '#999', padding: 8 }}>
            No hay notificaciones pendientes
          </div>
        </Menu.Item>
      )}
    </Menu>
  );

  const userMenu = (
    <Menu style={{ width: 280, padding: '12px 0' }}>
      <div style={{ padding: '0 16px 12px', textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>
        <Avatar size={60} icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
        <div style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 'bold', fontSize: 16, color: '#1a1a1a' }}>
            {user.nombreUsuario}
          </div>
          <div style={{ color: '#595959', fontSize: 13, margin: '4px 0' }}>
            {user.nombreCompleto}
          </div>
          <div style={{ color: colorRol, fontSize: 13, fontWeight: 600 }}>
            {roles[user.rol] || 'Cargando rol...'}
          </div>
        </div>
      </div>

      <Menu.Item key="change-password" icon={<LockOutlined />} onClick={() => setIsModalOpen(true)}>
        Cambiar contraseña
      </Menu.Item>

      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout} danger>
        Cerrar sesión
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      <Header style={{
        padding: '0 24px',
        background: '#001f3f',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        color: '#fff',
        height: 64,
        lineHeight: '64px'
      }}>
        <Dropdown overlay={notificationMenu} trigger={['click']}>
          <Badge count={notifications.length} size="small" offset={[-2, 2]}>
            <BellOutlined style={{ fontSize: 20, cursor: 'pointer', marginRight: 24, color: '#fff' }} />
          </Badge>
        </Dropdown>

        <LogoutOutlined
          style={{ fontSize: 20, cursor: 'pointer', marginRight: 24, color: '#fff' }}
          onClick={handleLogout}
          title="Cerrar sesión"
        />

        <Dropdown overlay={userMenu} trigger={['click']}>
          <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <span style={{ marginRight: 8, color: '#fff', fontWeight: 500 }}>
              {user.nombreUsuario}
            </span>
            <Avatar size={32} icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
          </div>
        </Dropdown>
      </Header>

      {/* Modal Cambiar Contraseña */}
      <Modal
        title="Cambiar Contraseña"
        open={isModalOpen}
        onCancel={handleCancel}
        onOk={() => form.submit()}
        okText="Cambiar"
        cancelText="Cancelar"
      >
        <Form form={form} layout="vertical" onFinish={handleChangePassword}>
          <Form.Item name="currentPassword" label="Contraseña Actual" rules={[{ required: true }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item name="newPassword" label="Nueva Contraseña" rules={[{ required: true, min: 6 }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirmar Nueva Contraseña"
            dependencies={['newPassword']}
            rules={[
              { required: true },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                  return Promise.reject(new Error('Las contraseñas no coinciden'));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default HeaderComponent;
